import React, { useState, useEffect, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { Loader2, AlertCircle } from 'lucide-react';
import type { Book } from '../../../shared/types/index.js';
import { api } from '../../../shared/api/api.js';
import { useAuthStore } from '../../auth/store/useAuthStore.js';
import { downloadCertificatePdf } from '../../uibn/utils/certificateGenerator.js';
import { SeoHead, useBookOgImage, createBookSchema } from '../../../shared/components/index.js';
import { slugify } from '../../../shared/utils/stringUtils.js';
import {
  VerificationHeaderBanner,
  BookVerificationCertificateCard,
  EmbedBadgePromoCard,
  VerificationFooterActions,
} from './book-verification/index.js';
import { useBodyScrollLock } from '../../../shared/utils/scrollLock.js';

interface BookVerificationModalProps {
  uibnOrId: string | null;
  isOpen: boolean;
  onClose: () => void;
  onOpenEditionTree?: (book: Book) => void;
  onOpenEmbedModal?: (book: Book) => void;
  onOpenFullDetail?: (book: Book) => void;
}

export const BookVerificationModal: React.FC<BookVerificationModalProps> = ({
  uibnOrId,
  isOpen,
  onClose,
  onOpenEditionTree,
  onOpenEmbedModal,
  onOpenFullDetail,
}) => {
  const { user } = useAuthStore();
  const [book, setBook] = useState<Book | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isDownloadingCert, setIsDownloadingCert] = useState(false);
  const [activeCodeTab, setActiveCodeTab] = useState<'qr' | 'barcode'>('qr');

  useBodyScrollLock(isOpen);

  useEffect(() => {
    if (!isOpen || !uibnOrId) {
      setBook(null);
      setError(null);
      return;
    }

    let isMounted = true;
    setIsLoading(true);
    setError(null);

    api.getBookById(uibnOrId)
      .then((res) => {
        if (isMounted) {
          setBook(res.book);
          api.recordScan(res.book.uibn, 'manual_input', res.book.id).catch(() => {});
        }
      })
      .catch((err) => {
        if (isMounted) {
          setError(err.message || 'Data verifikasi buku tidak ditemukan dalam pangkalan data resmi.');
        }
      })
      .finally(() => {
        if (isMounted) setIsLoading(false);
      });

    return () => {
      isMounted = false;
    };
  }, [isOpen, uibnOrId]);


  const handleDownloadPdf = async () => {
    if (!book) return;
    setIsDownloadingCert(true);
    try {
      await downloadCertificatePdf(book);
    } catch (err) {
      console.error('Failed to download certificate:', err);
    } finally {
      setIsDownloadingCert(false);
    }
  };

  const isAuthorOwner = Boolean(
    user?.role === 'penulis' && (
      book?.authorId === user.id ||
      (book?.authorName && book.authorName.toLowerCase() === user.name.toLowerCase())
    )
  );
  const isPublisherOwner = Boolean(
    user?.role === 'penerbit' && (
      book?.publisherId === user.id ||
      (user.organization && book?.publisherName && book.publisherName.toLowerCase() === user.organization.toLowerCase()) ||
      (book?.publisherName && book.publisherName.toLowerCase() === user.name.toLowerCase()) ||
      (user.publisherCode && book?.uibn && book.uibn.includes(user.publisherCode))
    )
  );
  const isAdmin = user?.role === 'admin';
  const isOwnerOrAdmin = Boolean(isAdmin || isAuthorOwner || isPublisherOwner);
  const isVerified = book?.status === 'disetujui';

  const bookOgImage = useBookOgImage(
    book
      ? {
          title: book.title,
          uibn: book.uibn,
          authorName: book.authorName,
          publisherName: book.publisherName,
          category: book.category,
          publicationYear: book.publicationYear,
          coverUrl: book.coverUrl || undefined,
        }
      : null
  );

  const bookStructuredData = useMemo(() => {
    if (!book) return undefined;
    const origin = typeof window !== 'undefined' ? window.location.origin : 'https://uibn.id';
    const bookUrl = `${origin}/buku/${slugify(book.title)}`;
    return createBookSchema({
      title: book.title,
      uibn: book.uibn,
      authorName: book.authorName,
      publisherName: book.publisherName,
      category: book.category,
      publicationYear: book.publicationYear,
      pages: book.pages || undefined,
      language: book.language || undefined,
      synopsis: book.synopsis || undefined,
      coverUrl: book.coverUrl || undefined,
      url: bookUrl,
    });
  }, [book]);

  const bookUrl = book ? `${typeof window !== 'undefined' ? window.location.origin : ''}/buku/${slugify(book.title)}` : undefined;

  const modalContent = (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center bg-slate-950/80 backdrop-blur-xs p-0 sm:p-5 animate-in fade-in duration-200"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      {book && (
        <SeoHead
          title={`Verifikasi UIBN: ${book.title}`}
          description={`Data pendaftaran resmi UIBN untuk buku "${book.title}" karya ${book.authorName}. Verifikasi nomor registrasi ${book.uibn} dan keaslian naskah secara instan.`}
          ogType="book"
          ogImage={bookOgImage || book.coverUrl || undefined}
          canonicalUrl={bookUrl}
          keywords={[book.title, book.uibn, book.authorName, book.publisherName, 'verifikasi naskah', 'registrasi buku', 'uibn indonesia']}
          structuredData={bookStructuredData}
        />
      )}

      <div className="bg-white rounded-none sm:rounded-3xl max-w-2xl w-full h-full sm:h-auto sm:max-h-[94vh] overflow-hidden shadow-2xl border-0 sm:border border-slate-200 flex flex-col">
        {/* Verification Status Header Banner */}
        <VerificationHeaderBanner
          isLoading={isLoading}
          isVerified={isVerified}
          error={error}
          onClose={onClose}
        />

        {/* Content Body */}
        <div className="p-4 sm:p-6 overflow-y-auto space-y-5 flex-1 bg-slate-50/60">
          {isLoading && (
            <div className="py-16 text-center space-y-3">
              <Loader2 className="w-10 h-10 animate-spin text-indigo-600 mx-auto" />
              <p className="text-sm font-semibold text-slate-600">
                Menghubungkan ke Pangkalan Data UIBN Nasional...
              </p>
            </div>
          )}

          {error && !isLoading && (
            <div className="p-6 bg-rose-50 border border-rose-200 rounded-2xl text-center space-y-3">
              <AlertCircle className="w-12 h-12 text-rose-500 mx-auto" />
              <h3 className="text-base font-bold text-rose-900">Naskah Tidak Ditemukan</h3>
              <p className="text-xs text-rose-700 max-w-md mx-auto leading-relaxed">
                {error} Pastikan kode UIBN diketik dengan format lengkap (contoh: <code>UIBN-979-8000-001-K</code>).
              </p>
            </div>
          )}

          {book && !isLoading && (
            <>
              {/* Authenticity Certificate Card */}
              <BookVerificationCertificateCard
                book={book}
                isOwnerOrAdmin={isOwnerOrAdmin}
                activeCodeTab={activeCodeTab}
                onSetActiveCodeTab={setActiveCodeTab}
              />

              {/* Publisher & Author Tools: Get Embed Badge */}
              {isOwnerOrAdmin && book.status !== 'diajukan' && (
                <EmbedBadgePromoCard
                  book={book}
                  onOpenEmbedModal={onOpenEmbedModal}
                />
              )}
            </>
          )}
        </div>

        {/* Modal Actions Footer */}
        <VerificationFooterActions
          book={book}
          isOwnerOrAdmin={isOwnerOrAdmin}
          isDownloadingCert={isDownloadingCert}
          onOpenFullDetail={onOpenFullDetail}
          onOpenEditionTree={onOpenEditionTree}
          onDownloadPdf={handleDownloadPdf}
          onClose={onClose}
        />
      </div>
    </div>
  );

  if (!isOpen) return null;

  return createPortal(modalContent, document.body);
};
